import { safeKeyComponent } from "./common";
import { storyPrefix, VideoPrefix } from "./path";

export function sanitizeMemoryPayload(data: any) {
    if (!data || typeof data !== "object") return data;

    const storyTitle = safeKeyComponent(data?.storyTitle || "untitled");

    // ---- helpers ----
    const prependIfNeeded = (kind: "video" | "audio" | "image", name: string) => {
        if (!name) return name;
        if (/^(videos|stories|audios)\//.test(name)) return name;
        const base =
            kind === "video"
                ? `${VideoPrefix}/${storyTitle}/memories/`
                : kind === "audio"
                    ? `${storyPrefix}/${storyTitle}/memories/`
                    : `${storyPrefix}/${storyTitle}/memories/`;
        return `${base}${name}`;
    };

    const isDbObject = (item: any) =>
        !!item?.fileName && (item?.status || item?.thumb || item?.versions);

    const inferKind = (item: any): "video" | "audio" | "image" => {
        const mime = item?.file?.type || "";
        if (mime.startsWith("audio/")) return "audio";
        if (mime.startsWith("image/")) return "image";
        if (mime.startsWith("video/")) return "video";
        const name = item?.file?.name || item?.fileName || "";
        if (/\.(mp3|wav|m4a|aac|flac|ogg|oga|opus)$/i.test(name)) return "audio";
        if (/\.(png|jpg|jpeg|webp|gif|heic|heif|bmp|svg)$/i.test(name)) return "image";
        return "video";
    };

    // canonical compare key (case/prefix insensitive)
    const canonicalKey = (fileName: string) =>
        fileName?.toLowerCase()
            .replace(/^https?:\/\/[^/]+\/+/, "")
            .replace(/^cdn\.[^/]+\/+/, "") || "";

    // ---- seed maps with DB items (kept verbatim) ----
    const dbDetails = data.memory_details || {};
    const dbVideos = Array.isArray(dbDetails.complementaryVideo) ? dbDetails.complementaryVideo : [];
    const dbImages = Array.isArray(dbDetails.complementaryImage) ? dbDetails.complementaryImage : [];
    const dbAudios = Array.isArray(dbDetails.complementaryAudio) ? dbDetails.complementaryAudio : [];

    const byKey = {
        video: new Map<string, any>(),
        image: new Map<string, any>(),
        audio: new Map<string, any>(),
    };

    for (const v of dbVideos) if (v?.fileName) byKey.video.set(canonicalKey(v.fileName), v);
    for (const i of dbImages) if (i?.fileName) byKey.image.set(canonicalKey(i.fileName), i);
    for (const a of dbAudios) if (a?.fileName) byKey.audio.set(canonicalKey(a.fileName), a);

    // ---- accumulate totalSize for NEW uploads ----
    let totalSize = 0;
    const addSize = (f: any) => {
        // prefer File.size, else raw size property if provided
        if (f?.file?.size && Number.isFinite(f.file.size)) totalSize += f.file.size;
        else if (Number.isFinite(f?.size)) totalSize += f.size;
    };

    // include top-level "media" file if present
    if (data?.media?.size && Number.isFinite(data.media.size)) totalSize += data.media.size;

    // include any legacy complementaryMedia sizes (if you still pass them)
    if (Array.isArray(data?.complementaryMedia)) {
        data.complementaryMedia.forEach(addSize);
    }

    // ---- process live mediaFiles (frontend) ----
    for (const f of data.mediaFiles || []) {
        const kind = inferKind(f);

        if (isDbObject(f)) {
            const key = canonicalKey(f.fileName);
            if (byKey[kind].has(key)) {
                // Update isFeatured from UI state (user may have changed it)
                const existing = byKey[kind].get(key);
                byKey[kind].set(key, { ...existing, isFeatured: !!f.isFeatured });
            } else {
                byKey[kind].set(key, f);
            }
            // DB object → no size counted (already uploaded)
            continue;
        }

        // new upload → minimal entry + size
        const baseName =
            f?.file?.name ||
            f?.name ||
            f?.fileName ||
            `file_${Date.now()}.${kind === "video" ? "mp4" : kind === "audio" ? "mp3" : "jpg"}`;
        const fullPath = prependIfNeeded(kind, baseName);
        const key = canonicalKey(fullPath);

        if (!byKey[kind].has(key)) {
            byKey[kind].set(key, { fileName: fullPath, isFeatured: !!f.isFeatured });
        }
        addSize(f);
    }

    // ---- build arrays back ----
    const complementaryVideo = Array.from(byKey.video.values());
    const complementaryImage = Array.from(byKey.image.values());
    const complementaryAudio = Array.from(byKey.audio.values());

    // ---- final cleaned payload ----
    const cleanedPayload: any = {
        ...data,
        memory_details: {
            complementaryVideo,
            complementaryImage,
            complementaryAudio,
            complementaryText: data?.richText, // mirror as requested
        },
        complementaryMedia: [],
        totalSize, // <= here!
    };

    // tidy: drop redundant top-level arrays if you don’t want to post them
    delete cleanedPayload.mediaFiles;
    delete cleanedPayload.complementaryMedia;
    delete cleanedPayload.complementaryAudio;
    delete cleanedPayload.complementaryImage;
    delete cleanedPayload.complementaryVideo;
    delete cleanedPayload.complementaryText;

    return cleanedPayload;
}

type MemoryType = 'text' | 'image' | 'video' | 'audio';

export function deriveMemoryType(memory_details: any): MemoryType {
    const imgs = Array.isArray(memory_details?.complementaryImage) ? memory_details.complementaryImage : [];
    const vids = Array.isArray(memory_details?.complementaryVideo) ? memory_details.complementaryVideo : [];
    const auds = Array.isArray(memory_details?.complementaryAudio) ? memory_details.complementaryAudio : [];

    const hasFeaturedImage = imgs.some((i: any) => i?.isFeatured === true);
    const hasFeaturedVideo = vids.some((v: any) => v?.isFeatured === true);

    const hasAnyMedia = imgs.length > 0 || vids.length > 0 || auds.length > 0;
    const hasText =
        (Array.isArray(memory_details?.richText) && memory_details.richText.length > 0) ||
        (Array.isArray(memory_details?.complementaryText) && memory_details.complementaryText.length > 0);

    if (hasFeaturedImage) return 'image';
    if (hasFeaturedVideo) return 'video';
    if (hasText && !hasAnyMedia) return 'text';
    if (vids.length > 0) return 'video';
    if (auds.length > 0) return 'audio';
    return 'text';
}
